import { IconButton, Stack, useTheme } from "@mui/material";
import {
	Ellipsis,
	Info,
	MessageCircle,
	NotebookPen,
	UsersRound,
} from "lucide-react";
import { useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { ActivityParticipants } from "web_myplann/Activities/Components/ActivityParticipants";
import { getAgentsWithCreator } from "web_myplann/Activities/Helpers/getAgentsWithCreator";
import { useFormatDateTimeByClientActivity } from "web_myplann/Activities/Hooks/useActivitiesFormatDateAndTimes";
import { useActivityMenuItems } from "web_myplann/Activities/Hooks/useActivityMenuItems";
import { useHasMultipleConnectedAgents } from "web_myplann/Activities/Hooks/useHasMultipleConnectedAgents";
import { useActivityAgentsQuery } from "web_myplann/Api/Activities/useActivityAgentsQuery";
import { useActivityNotesQuery } from "web_myplann/Api/Activities/useActivityNotesQuery";
import { useActivityParticipantsQuery } from "web_myplann/Api/Activities/useActivityParticipantsQuery";
import { useActivityQuery } from "web_myplann/Api/Activities/useActivityQuery";
import { useAgentQuery } from "web_myplann/Api/Advisor/useAgentQuery";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailSectionLoader } from "web_myplann/Portfolio/Components/DetailSectionLoader";
import { DataCard } from "web_myplann/Shared/Components/DataCard";
import { DataNote } from "web_myplann/Shared/Components/DataNote";
import { DataRow } from "web_myplann/Shared/Components/DataRow";
import { ToolBarMenu } from "web_myplann/Shared/Components/ToolBarMenu";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";
import { useFormatUserWithType } from "web_myplann/Shared/Hooks/useFormatUserWithType";
import { useOpenAgentChat } from "web_myplann/Shared/Hooks/useOpenAgentChat";
import { getBackFallbackRoute } from "web_myplann/Shared/Utils/backNavigation";
import { getAgentChatTitle } from "web_myplann/Shared/Utils/getAgentChatTitle";
import { getTypedParam } from "web_myplann/Shared/Utils/getTypedParam";
import { getMapsLink } from "web_myplann/Utils/getMapsLink";

export const ActivityDetailPage = () => {
	const routeParams = useParams<{ id: string }>();
	const activityId = getTypedParam(routeParams, "id", Number) ?? null;
	const theme = useTheme();
	const { t } = useResource();
	const formatDateTime = useFormatDateTimeByClientActivity();
	const location = useLocation();

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

	const { data: dataEvent, isLoading: isLoadingEvent } =
		useActivityQuery(activityId);
	const { data: dataAgents, isLoading: isLoadingAgents } =
		useActivityAgentsQuery(activityId);
	const { data: dataNotes, isLoading: isLoadingNotes } =
		useActivityNotesQuery(activityId);
	const { data: dataParticipants, isLoading: isLoadingParticipants } =
		useActivityParticipantsQuery(activityId);

	const { data: agentDetail } = useAgentQuery(dataEvent?.author?.id);
	const hasMultipleConnectedAgents = useHasMultipleConnectedAgents();
	const formatUserWithType = useFormatUserWithType();
	const openAgentChat = useOpenAgentChat();

	const activityType = dataEvent?.type
		? t(Resources.Activity.List.ActivityType[dataEvent.type])
		: "";

	const menuItems = useActivityMenuItems(agentDetail);

	const agents = getAgentsWithCreator(
		dataAgents?.data ?? [],
		dataEvent?.creator,
	);

	const mapsUrl = getMapsLink(dataEvent?.place ?? "");

	if (activityId === null) return null;

	return (
		<>
			<MyPlannLayout
				topBar={
					<NavigationHeader
						title={t(Resources.Activity.Detail.title)}
						defaultRoute={getBackFallbackRoute(location.pathname)}
						replaceOnBack
						endComponent={
							<Stack direction="row" gap={1.6}>
								<IconButton
									onClick={() =>
										openAgentChat({
											uid: agentDetail?.uid,
											title: getAgentChatTitle(agentDetail),
											conversationId: agentDetail?.conversationId,
										})
									}
									sx={{ padding: 0 }}
								>
									<MessageCircle
										cursor="pointer"
										color={theme.palette.primary.main}
									/>
								</IconButton>
								<IconButton
									sx={{ padding: 0 }}
									onClick={(event) => {
										event.stopPropagation();
										setAnchorEl(event.currentTarget);
									}}
									disableFocusRipple
									disableTouchRipple
								>
									<Ellipsis
										cursor="pointer"
										color={theme.palette.primary.main}
									/>
								</IconButton>
							</Stack>
						}
					/>
				}
			>
				{isLoadingEvent ||
				isLoadingNotes ||
				isLoadingParticipants ||
				isLoadingAgents ? (
					<DetailSectionLoader />
				) : (
					dataEvent && (
						<Stack paddingTop={3.2}>
							<DataCard
								icon={
									<Info
										size={theme.iconSize.sm}
										color={theme.palette.deprecated.grey[700]}
									/>
								}
								label={t(Resources.Activity.Detail.BasicInfo.title)}
							>
								{dataEvent?.title && (
									<DataRow
										label={t(Resources.Activity.Detail.BasicInfo.Name)}
										value={dataEvent?.title}
									/>
								)}
								<DataRow
									label={t(Resources.Activity.Detail.BasicInfo.Type)}
									value={activityType}
								/>
								<DataRow
									label={t(Resources.Activity.Detail.BasicInfo.Date)}
									value={formatDateTime(dataEvent)}
								/>
								<DataRow
									label={t(Resources.Activity.Detail.BasicInfo.Address)}
									value={dataEvent.place}
									link={mapsUrl}
								/>
								{hasMultipleConnectedAgents && (
									<DataRow
										label={t(Resources.Activity.Detail.BasicInfo.CreatedBy)}
										value={formatUserWithType(dataEvent.author)}
									/>
								)}
							</DataCard>

							<DataCard
								icon={<UsersRound size={theme.iconSize.sm} />}
								label={t(Resources.Activity.Detail.Participants.title)}
								noDivider={dataNotes?.data.length === 0}
								isEmpty={!dataParticipants?.data.length && !agents.length}
							>
								<ActivityParticipants
									participants={dataParticipants?.data ?? []}
									agents={agents}
								/>
							</DataCard>

							<DataCard
								icon={<NotebookPen size={theme.iconSize.sm} />}
								label={t(Resources.Activity.Detail.notes)}
								noDivider
								isEmpty={!dataNotes?.data.some((note) => !!note.text)}
							>
								<Stack direction="column" gap={0.8}>
									{dataNotes?.data.map((n) => (
										<DataNote key={n.id} label={n.title} text={n.text} />
									))}
								</Stack>
							</DataCard>
						</Stack>
					)
				)}
			</MyPlannLayout>
			<ToolBarMenu
				anchorEl={anchorEl}
				setAnchorEl={setAnchorEl}
				items={menuItems}
			/>
		</>
	);
};
